// App preferences

"use strict";

import {
    addAppEventListener,
    emitAppEvent,
    EVENT_NAME_ALBUM_SIDEBAR_TOP,
    EVENT_NAME_ALBUMS_LIST_UPDATE,
    EVENT_NAME_CURRENT_ALBUM_UPDATED,
    EVENT_NAME_FAVORITE_ALBUMS_UPDATED,
    EVENT_NAME_PAGE_PREFERENCES_UPDATED,
    EVENT_NAME_THEME_CHANGED,
} from "../global-state/app-events";
import { clearLocalStorage, fetchFromLocalStorage, fetchFromLocalStorageCache, saveIntoLocalStorage } from "@/local-storage/local-storage";
import type { Album, AlbumListItemMin } from "@/api/models";
import type { NavigationStatusPage } from "@/global-state/navigation";
import type { SessionDuration } from "@/api/api-auth";

export type ColorThemeName = "light" | "dark";

const DEFAULT_THEME = "dark";

const LS_KEY_THEME = "app-pref-theme";

/**
 * Gets color theme
 * @returns The theme name
 */
export function getTheme(): ColorThemeName {
    return fetchFromLocalStorageCache(LS_KEY_THEME, DEFAULT_THEME);
}

/**
 * Sets color theme
 * @param theme The theme name
 */
export function setTheme(theme: ColorThemeName) {
    saveIntoLocalStorage(LS_KEY_THEME, theme);
    emitAppEvent(EVENT_NAME_THEME_CHANGED, theme);
}

const DEFAULT_SESSION_DURATION: SessionDuration = "day";

const LS_KEY_SESSION_DURATION = "app-pref-session-duration";

/**
 * Gets the preferred session duration
 * @returns The preferred session duration
 */
export function getPreferredSessionDuration(): SessionDuration {
    const duration = fetchFromLocalStorageCache(LS_KEY_SESSION_DURATION, DEFAULT_SESSION_DURATION);

    if (!["day", "week", "month", "year"].includes(duration)) {
        return DEFAULT_SESSION_DURATION;
    }

    return duration;
}

/**
 * Sets the preferred session duration
 * @param duration The preferred session duration
 */
export function setPreferredSessionDuration(duration: SessionDuration) {
    saveIntoLocalStorage(LS_KEY_SESSION_DURATION, duration);
}

const LS_KEY_FAVORITE_ALBUMS = "app-pref-albums-fav";

/**
 * Checks if album is favorite
 * @param id Album ID
 * @returns True if the album is in favorites
 */
export function albumIsFavorite(id: number) {
    const idString = id + "";
    let favorites = fetchFromLocalStorage(LS_KEY_FAVORITE_ALBUMS, []);

    if (!favorites || !Array.isArray(favorites)) {
        favorites = [];
    }

    return favorites.includes(idString);
}

/**
 * Gets the list of favorites albums
 * @returns List of favorites albums
 */
export function getAlbumFavoriteList() {
    let favorites = fetchFromLocalStorage(LS_KEY_FAVORITE_ALBUMS, []);

    if (!favorites || !Array.isArray(favorites)) {
        favorites = [];
    }

    return favorites;
}

/**
 * Adds album to favorites
 * @param id Album ID
 */
export function albumAddFav(id: number) {
    const idString = id + "";
    let favorites = fetchFromLocalStorage(LS_KEY_FAVORITE_ALBUMS, []);

    if (!favorites || !Array.isArray(favorites)) {
        favorites = [];
    }

    if (!favorites.includes(idString)) {
        favorites.push(idString);
        saveIntoLocalStorage(LS_KEY_FAVORITE_ALBUMS, favorites);
        emitAppEvent(EVENT_NAME_FAVORITE_ALBUMS_UPDATED);
    }
}

/**
 * Removes albums from favorites
 * @param id Album ID
 */
export function albumRemoveFav(id: number) {
    const idString = id + "";
    let favorites = fetchFromLocalStorage(LS_KEY_FAVORITE_ALBUMS, []);

    if (!favorites || !Array.isArray(favorites)) {
        favorites = [];
    }

    const index = favorites.indexOf(idString);
    if (index >= 0) {
        favorites.splice(index, 1);
        saveIntoLocalStorage(LS_KEY_FAVORITE_ALBUMS, favorites);
        emitAppEvent(EVENT_NAME_FAVORITE_ALBUMS_UPDATED);
    }
}

/**
 * Clears list of favorite albums
 */
export function clearFavAlbums() {
    clearLocalStorage(LS_KEY_FAVORITE_ALBUMS);
    emitAppEvent(EVENT_NAME_FAVORITE_ALBUMS_UPDATED);
}

addAppEventListener(EVENT_NAME_ALBUMS_LIST_UPDATE, (albums: Map<number, AlbumListItemMin>) => {
    // Remove favorite albums removed from the vault
    let favorites = fetchFromLocalStorage(LS_KEY_FAVORITE_ALBUMS, []);

    if (!favorites || !Array.isArray(favorites)) {
        favorites = [];
    } else {
        favorites = favorites.filter((id) => {
            return albums.has(parseInt(id, 10));
        });
    }

    saveIntoLocalStorage(LS_KEY_FAVORITE_ALBUMS, favorites);
});

const LS_KEY_ALBUMS_ORDER = "app-pref-albums-order";

const ALBUM_ORDER_MAP_SIZE_LIMIT = 100;

/**
 * Gets albums ordering map.
 * Assigns each album the timestamp it was last loaded in.
 * @returns
 */
export function getAlbumsOrderMap(): { [id: string]: number } {
    let m = fetchFromLocalStorage(LS_KEY_ALBUMS_ORDER, Object.create(null));

    if (!m || typeof m !== "object") {
        m = Object.create(null);
    }

    return m;
}

/**
 * Clears album ordering map
 */
export function clearAlbumsOrderMap() {
    clearLocalStorage(LS_KEY_ALBUMS_ORDER);
}

addAppEventListener(EVENT_NAME_CURRENT_ALBUM_UPDATED, (albumData: Album | null) => {
    if (!albumData) {
        return;
    }

    const idString = albumData.id + "";
    const m = getAlbumsOrderMap();

    m[idString] = Date.now();

    while (Object.keys(m).length > ALBUM_ORDER_MAP_SIZE_LIMIT) {
        let oldest: string;
        let oldestVal = Date.now();

        for (const albumIdString of Object.keys(m)) {
            const v = m[albumIdString];

            if (v < oldestVal) {
                oldestVal = v;
                oldest = albumIdString;
            }
        }

        delete m[oldest];
    }

    saveIntoLocalStorage(LS_KEY_ALBUMS_ORDER, m);

    emitAppEvent(EVENT_NAME_ALBUM_SIDEBAR_TOP, albumData.id);
});

const LS_KEY_PAGE_SETTINGS = "app-pref-page-settings";

/**
 * Page preferences
 */
export interface PagePreferences {
    /**
     * Max items per page
     */
    pageSize: number;

    /**
     * Row size when expanded
     */
    rowSize: number;

    /**
     * Row size when split mode
     */
    rowSizeMin: number;

    /**
     * Min size of items
     */
    minItemSize: number;

    /**
     * Max size of items
     */
    maxItemSize: number;

    /**
     * Display titles?
     */
    displayTitles: boolean;

    /**
     * Rounded corners?
     */
    roundedCorners: boolean;

    /**
     * Padding for items
     */
    padding: number;
}

/**
 * Default page preferences
 */
const DEFAULT_PAGE_PREFERENCES: PagePreferences = {
    pageSize: 25,
    rowSize: 5,
    rowSizeMin: 2,
    minItemSize: 64,
    maxItemSize: 464,
    displayTitles: true,
    roundedCorners: true,
    padding: 18,
};

/**
 * Gets the page preferences
 * @param page The page name
 * @returns The page preferences
 */
export function getPagePreferences(page: NavigationStatusPage): PagePreferences {
    if (window.innerWidth > 1000) {
        DEFAULT_PAGE_PREFERENCES.rowSize = Math.max(2, Math.round((window.innerWidth - 240) / 250));
    } else {
        DEFAULT_PAGE_PREFERENCES.rowSize = Math.max(2, Math.round(window.innerWidth / 250));
    }

    DEFAULT_PAGE_PREFERENCES.pageSize = Math.min(256, DEFAULT_PAGE_PREFERENCES.rowSize * 6);

    let preferences =
        fetchFromLocalStorageCache(
            LS_KEY_PAGE_SETTINGS + "-" + page,
            fetchFromLocalStorageCache(LS_KEY_PAGE_SETTINGS, DEFAULT_PAGE_PREFERENCES),
        ) || DEFAULT_PAGE_PREFERENCES;

    if (typeof preferences !== "object" || Array.isArray(preferences)) {
        preferences = DEFAULT_PAGE_PREFERENCES;
    }

    for (const key of Object.keys(DEFAULT_PAGE_PREFERENCES)) {
        if (preferences[key] === undefined || typeof preferences[key] !== typeof DEFAULT_PAGE_PREFERENCES[key]) {
            preferences[key] = DEFAULT_PAGE_PREFERENCES[key];
        }
    }

    if (isNaN(preferences.pageSize) || preferences.pageSize < 1 || preferences.pageSize > 256) {
        preferences.pageSize = DEFAULT_PAGE_PREFERENCES.pageSize;
    }

    if (isNaN(preferences.rowSize) || preferences.rowSize < 1 || preferences.rowSize > 256) {
        preferences.rowSize = DEFAULT_PAGE_PREFERENCES.rowSize;
    }

    if (isNaN(preferences.rowSizeMin) || preferences.rowSizeMin < 1 || preferences.rowSizeMin > 256) {
        preferences.rowSizeMin = DEFAULT_PAGE_PREFERENCES.rowSizeMin;
    }

    if (isNaN(preferences.minItemSize) || preferences.minItemSize < 1 || preferences.minItemSize > 1000) {
        preferences.minItemSize = DEFAULT_PAGE_PREFERENCES.minItemSize;
    }

    if (isNaN(preferences.maxItemSize) || preferences.maxItemSize < 1 || preferences.maxItemSize > 1000) {
        preferences.maxItemSize = DEFAULT_PAGE_PREFERENCES.maxItemSize;
    }

    if (isNaN(preferences.padding) || preferences.padding < 0 || preferences.padding > 32) {
        preferences.maxItemSize = DEFAULT_PAGE_PREFERENCES.maxItemSize;
    }

    return preferences;
}

/**
 * Sets the page preferences
 * @param page The page name
 * @param preferences The page preferences
 */
export function setPagePreferences(page: NavigationStatusPage, preferences: PagePreferences) {
    saveIntoLocalStorage(LS_KEY_PAGE_SETTINGS + "-" + page, preferences);
    emitAppEvent(EVENT_NAME_PAGE_PREFERENCES_UPDATED);
}

/**
 * Resets page preferences
 * @param page Page name
 */
export function resetPagePreferences(page: NavigationStatusPage) {
    clearLocalStorage(LS_KEY_PAGE_SETTINGS + "-" + page);
    clearLocalStorage(LS_KEY_PAGE_SETTINGS);
    emitAppEvent(EVENT_NAME_PAGE_PREFERENCES_UPDATED);
}

const LS_KEY_LAST_USED_TAGS = "app-last-used-tags";
const LS_KEY_LAST_USED_TAGS_SEARCH = "app-last-search-tags";

const LAST_USED_TAGS_LIMIT = 64;

/**
 * Gets the key to store the last used tags
 * @param mode The mode
 * @returns The key
 */
function getLastUsedTagsKey(mode: LastUsedTagMode): string {
    if (mode === "search") {
        return LS_KEY_LAST_USED_TAGS_SEARCH;
    } else {
        return LS_KEY_LAST_USED_TAGS;
    }
}

/**
 * The last used tag mode:
 *  - 'search' - Tags being searched for
 *  - 'edit' - Tags being added to media
 */
export type LastUsedTagMode = "search" | "edit";

/**
 * Gets the list of last used tags
 * @param mode The mode
 */
export function getLastUsedTags(mode: LastUsedTagMode): number[] {
    let r = fetchFromLocalStorageCache(getLastUsedTagsKey(mode), []);

    if (!Array.isArray(r)) {
        r = [];
    }

    return r;
}

/**
 * Updates the last used tags
 * @param tag The used tag
 * @param mode The mode
 */
export function setLastUsedTag(tag: number, mode: LastUsedTagMode) {
    const key = getLastUsedTagsKey(mode);

    let r = fetchFromLocalStorage(key, []);

    if (!Array.isArray(r)) {
        r = [];
    }

    if (r.includes(tag)) {
        r = r.filter((t) => t !== tag);
    }

    r.unshift(tag);

    while (r.length > LAST_USED_TAGS_LIMIT) {
        r.pop();
    }

    saveIntoLocalStorage(key, r);
}

/**
 * Clears list of recently used tags
 */
export function clearLastUsedTags() {
    clearLocalStorage(LS_KEY_LAST_USED_TAGS);
    clearLocalStorage(LS_KEY_LAST_USED_TAGS_SEARCH);
}

/**
 * Clears page preferences
 */
export function clearPagePreferences() {
    clearLocalStorage(LS_KEY_THEME);
    clearLocalStorage(LS_KEY_SESSION_DURATION);
    emitAppEvent(EVENT_NAME_THEME_CHANGED, DEFAULT_THEME);

    ["home", "media", "random", "random", "albums", "upload", "search"].forEach(resetPagePreferences);
}

const DEFAULT_MAX_PARALLEL_UPLOADS = 1;

const LS_KEY_MAX_PARALLEL_UPLOADS = "app-pref-parallel-uploads";

/**
 * Gets the max parallel uploads setting
 * @returns The max parallel uploads
 */
export function getMaxParallelUploads(): number {
    return Math.max(1, Number(fetchFromLocalStorageCache(LS_KEY_MAX_PARALLEL_UPLOADS, DEFAULT_MAX_PARALLEL_UPLOADS)));
}

/**
 * Sets the max parallel uploads setting
 * @param maxParallelUploads The max parallel uploads
 */
export function setMaxParallelUploads(maxParallelUploads: number) {
    saveIntoLocalStorage(LS_KEY_MAX_PARALLEL_UPLOADS, maxParallelUploads);
}

/**
 * Clears preferences for the upload page
 */
export function clearUploadPreferences() {
    clearLocalStorage(LS_KEY_MAX_PARALLEL_UPLOADS);
}

/**
 * Search mode for search page
 */
export type SearchMode = "basic" | "adv" | "semantic" | "image";

/**
 * List of available search modes
 */
export const SEARCH_MODES: SearchMode[] = ["basic", "adv", "semantic", "image"];

/**
 * List of available search modes (unconditional)
 */
export const SEARCH_MODES_UNCONDITIONAL: SearchMode[] = ["basic", "adv", "semantic"];

export const DEFAULT_SEARCH_MODE = "basic";

const LS_KEY_SEARCH_MODE = "app-pref-search-mode";

/**
 * Gets the preferred search mode for media
 * @param semanticSearchAvailable True if semantic search is available
 * @returns The search mode
 */
export function getPreferredSearchMode(semanticSearchAvailable: boolean): SearchMode {
    const searchMode = fetchFromLocalStorageCache(LS_KEY_SEARCH_MODE, DEFAULT_SEARCH_MODE) + "";

    if (!SEARCH_MODES.includes(searchMode as SearchMode)) {
        return DEFAULT_SEARCH_MODE;
    }

    if (!semanticSearchAvailable && (searchMode === "semantic" || searchMode === "image")) {
        return DEFAULT_SEARCH_MODE;
    }

    return searchMode as SearchMode;
}

/**
 * Sets the preferred search mode for media
 * @param mode The search mode
 */
export function setPreferredSearchMode(mode: SearchMode) {
    saveIntoLocalStorage(LS_KEY_SEARCH_MODE, mode);

    if (SEARCH_MODES_UNCONDITIONAL.includes(mode)) {
        setPreferredSearchModeUnconditional(mode);
    }
}

const LS_KEY_SEARCH_MODE_UC = "app-pref-search-mode-unconditional";

/**
 * Gets the preferred search mode for media
 * (unconditional mode)
 * @param semanticSearchAvailable True if semantic search is available
 * @returns The search mode
 */
export function getPreferredSearchModeUnconditional(semanticSearchAvailable: boolean): SearchMode {
    const searchMode = fetchFromLocalStorageCache(LS_KEY_SEARCH_MODE_UC, DEFAULT_SEARCH_MODE) + "";

    if (!SEARCH_MODES_UNCONDITIONAL.includes(searchMode as SearchMode)) {
        return DEFAULT_SEARCH_MODE;
    }

    if (!semanticSearchAvailable && searchMode === "semantic") {
        return DEFAULT_SEARCH_MODE;
    }

    return searchMode as SearchMode;
}

/**
 * Sets the preferred search mode for media
 * (unconditional mode)
 * @param mode The search mode
 */
export function setPreferredSearchModeUnconditional(mode: SearchMode) {
    saveIntoLocalStorage(LS_KEY_SEARCH_MODE_UC, mode);
}

/**
 * Clears preferences for the search page
 */
export function clearSearchPreferences() {
    clearLocalStorage(LS_KEY_SEARCH_MODE);
    clearLocalStorage(LS_KEY_SEARCH_MODE_UC);
}
